import { useRef, useState } from 'react'

import { Box, Table, colors } from '@factorialco/deprecated-design-system'
import { isEqual } from 'lodash'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import useSubscribe from 'modules/core/hooks/useSubscribe'
import i18n from 'modules/core/lib/i18n'

import { EmptyStateProps } from 'modules/finance/components/FinancialWorkspace/components/Table/types'
import { useTrackReportRun } from 'modules/finance/components/FinancialWorkspace/hooks/useTrackReportRun'

import { useFinancialWorkspace } from '../../../Providers/Provider'
import { useCurrentPage } from '../../../hooks/useCurrentPage'
import {
  InsightTableGroupBy,
  InsightTableType,
  TableDataVariables,
  TableProps,
} from '../../../types/table'
import { REFETCH_DATA_EVENT, resolveItemsPerPage } from '../const'
import { EmptyStateTable } from './EmptyState'
import { Header } from './Header'
import { Loading } from './Loading'
import { NestedRows } from './NestedRows'
import { LoadingRow } from './Row/LoadingRow'
import { Ungrouped } from './Row/Ungrouped'
import { useVisitTracking } from './hooks/useVisitTracking'

interface PaginatedTableProps {
  title: string
  search: string
  formattedState: TableDataVariables
  type?: InsightTableType
  group: InsightTableGroupBy
  emptyStateProps: EmptyStateProps
  defaultGroupBy: InsightTableGroupBy
  useDataFetch: (variables: TableDataVariables) => TableProps
}

const PaginatedTable: React.FC<PaginatedTableProps> = ({
  type,
  group,
  title,
  search,
  useDataFetch,
  formattedState,
  defaultGroupBy,
  emptyStateProps,
}) => {
  const [initialVariables, setInitialVariables] = useState({})
  const [currentPage, setCurrentPage] = useState<number>(0)
  const trackReportRun = useTrackReportRun()
  const currentRoute = useCurrentPage()
  const itemsPerPage = resolveItemsPerPage(type, group)
  const [lastCursor, setLastCursor] = useState<string | null | undefined>(null)
  const tableHeadRef = useRef<HTMLTableSectionElement>(null)

  const { hasComparisonDate } = useFinancialWorkspace()

  const resetPagination = () => {
    // Reset page to 1 when filters change
    setCurrentPage(0)
    setLastCursor(null)
  }

  const variables = {
    ...formattedState,
    search,
    itemsPerPage: itemsPerPage,
    after: lastCursor,
    groupBy: group,
    resetPagination,
  }

  useUnsafeEffect(() => {
    resetPagination()
  }, [JSON.stringify(formattedState), variables.search, group])

  const isFirstRender = useRef(true)
  const filtersApplied = !isFirstRender.current && !isEqual(variables, initialVariables)

  const { loading, items, headers, refetch, hasNextPage, endCursor, totalCount } =
    useDataFetch(variables)

  useSubscribe([REFETCH_DATA_EVENT], () => {
    refetch()
  })

  const reportRun = useUnsafeCallback(
    (value: boolean) => {
      const {
        period,
        formattedStartDate,
        formattedEndDate,
        costCenterIds,
        locationIds,
        conceptIds,
        legalEntityIds,
        employeeIds,
      } = formattedState

      trackReportRun(value, {
        period,
        page: currentRoute,
        group,
        formattedStartDate,
        formattedEndDate,
        legalEntityIds,
        costCenterIds,
        locationIds,
        conceptIds,
        employeeIds,
        search,
      })
    },
    [variables]
  )

  useVisitTracking({ loading, items })

  useEffect(() => {
    setInitialVariables(variables)
    reportRun(true)

    isFirstRender.current = false
  }, [])

  useUnsafeEffect(() => {
    if (filtersApplied && !isFirstRender.current) {
      reportRun(false)
    }
  }, [items])

  if (loading && !lastCursor) {
    return <Loading />
  }

  if (items.length === 0) {
    return (
      <EmptyStateTable
        filtersApplied={filtersApplied || !!search}
        emptyStateProps={emptyStateProps}
      />
    )
  }

  const loadMore = (page: number) => {
    if (lastCursor !== endCursor && !loading) {
      setLastCursor(endCursor)
      setCurrentPage(page)
    }
  }

  return (
    <Table
      noTopRadius
      hideBorder
      autoWidth
      style={{
        borderTop: `1px solid ${colors.grey400}`,
      }}
    >
      <Table.Head ref={tableHeadRef}>
        <Header
          title={title}
          headers={headers}
          totalCount={totalCount}
          isGrouped={group !== defaultGroupBy}
        />
      </Table.Head>
      <Table.BodyWithInfiniteScroll
        page={currentPage}
        loadMore={loadMore}
        hasMore={hasNextPage}
        colSpan={headers.length + 1}
      >
        <NestedRows
          level={0}
          items={items}
          groupBy={group}
          tableHeadRef={tableHeadRef}
          currentPageCount={items.length}
          headerCount={headers.length / (hasComparisonDate ? 3 : 1)}
        />

        {!hasNextPage && <Ungrouped items={items} />}

        {loading &&
          Array.from({ length: variables.itemsPerPage ?? 0 }).map((_, index) => (
            <LoadingRow key={index} level={0} />
          ))}
      </Table.BodyWithInfiniteScroll>
      <Box height="s48" component="nav" aria-label={i18n.t('common.pagination.wrapper')}></Box>
    </Table>
  )
}

export default PaginatedTable
